import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall } from '../utils/customerUtils'
import ModalShell from './ui/ModalShell'
import ModalFooter from './ui/ModalFooter'
import { buttonVariants } from './ui/buttonVariants'

interface Task {
  id: number
  description: string
  due_date: string
  customer_id: number | null
  customer_name: string | null
  deal_id?: number
  lead_id?: number
  priority: string
}

interface CompleteTaskModalProps {
  isOpen: boolean
  task: Task | null
  onClose: () => void
  /** Called after the task is marked complete (with or without follow-up) */
  onRefresh: () => Promise<void>
}

type Priority = 'low' | 'medium' | 'high'

function formatLocalDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getNext7Days(language: string) {
  const days = []
  const today = new Date()
  for (let i = 0; i < 7; i++) {
    const date = new Date(today)
    date.setDate(today.getDate() + i)
    const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    days.push({ date: formatLocalDate(date), label, isToday: i === 0, isTomorrow: i === 1 })
  }
  return days
}

const CompleteTaskModal: React.FC<CompleteTaskModalProps> = ({ isOpen, task, onClose, onRefresh }) => {
  const { t } = useTranslation()
  const language = document.documentElement.lang || 'en'

  const [followUpDescription, setFollowUpDescription] = useState('')
  const [followUpPriority, setFollowUpPriority] = useState<Priority>('medium')
  const [followUpSelectedDate, setFollowUpSelectedDate] = useState('')
  const [followUpCustomDate, setFollowUpCustomDate] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setFollowUpDescription('')
    setFollowUpPriority('medium')
    setFollowUpSelectedDate('')
    setFollowUpCustomDate('')
    setError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const completeOnly = async () => {
    if (!task) return
    setSubmitting(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      const res = await makeApiCall(`api/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'completed' }),
      })
      if (!res.ok) throw new Error('Failed to complete task')
      handleClose()
      await onRefresh()
    } catch (err: any) {
      setError(err.message || 'Failed to complete task')
    } finally {
      setSubmitting(false)
    }
  }

  const completeWithFollowUp = async () => {
    if (!task || !followUpDescription.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')

      // Determine follow-up due date
      let dueDate: string
      if (followUpCustomDate) {
        dueDate = followUpCustomDate
      } else if (followUpSelectedDate) {
        dueDate = followUpSelectedDate
      } else {
        const d = new Date()
        d.setDate(d.getDate() + 1)
        dueDate = formatLocalDate(d)
      }

      const followUpData: Record<string, unknown> = {
        customer_id: task.customer_id,
        parent_task_id: task.id,
        description: followUpDescription,
        due_date: dueDate,
        priority: followUpPriority,
        status: 'pending',
        task_type: 'follow_up',
      }
      if (task.deal_id) followUpData.deal_id = task.deal_id
      if (task.lead_id) followUpData.lead_id = task.lead_id

      const [fuRes, completeRes] = await Promise.all([
        makeApiCall('api/tasks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(followUpData),
        }),
        makeApiCall(`api/tasks/${task.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status: 'completed' }),
        }),
      ])

      if (!fuRes.ok) throw new Error('Failed to create follow-up task')
      if (!completeRes.ok) throw new Error('Failed to complete task')

      handleClose()
      await onRefresh()
    } catch (err: any) {
      setError(err.message || 'Failed')
    } finally {
      setSubmitting(false)
    }
  }

  const days7 = getNext7Days(language)
  const PRIORITIES: Priority[] = ['low', 'medium', 'high']

  return (
    <ModalShell
      isOpen={isOpen && task !== null}
      onClose={handleClose}
      title={t('tasks.modals.taskCompleted', { defaultValue: 'Task Complete' })}
      size="lg"
    >
      {task && (
        <div className="space-y-5">
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              {error}
            </p>
          )}

          {/* Completed task summary */}
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 flex items-start gap-3">
            <span className="mt-0.5 flex-shrink-0 w-6 h-6 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center text-emerald-700">✓</span>
            <div>
              <p className="font-medium text-emerald-800 dark:text-emerald-200 text-sm">{task.description}</p>
              {task.customer_name && <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">{task.customer_name}</p>}
            </div>
          </div>

          {/* Follow-up description */}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              {t('tasks.modals.scheduleFollowUp', { defaultValue: 'Schedule a follow-up (optional)' })}
            </p>
            <textarea
              value={followUpDescription}
              onChange={(e) => setFollowUpDescription(e.target.value)}
              placeholder={t('tasks.modals.followUpPlaceholder', { defaultValue: 'Describe the follow-up task…' })}
              rows={2}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.metaKey && followUpDescription.trim()) {
                  e.preventDefault()
                  completeWithFollowUp()
                }
              }}
            />
          </div>

          {followUpDescription.trim() && (
            <>
              {/* Priority */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Priority</p>
                <div className="flex gap-2">
                  {PRIORITIES.map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setFollowUpPriority(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors border-2 capitalize ${
                        followUpPriority === p
                          ? p === 'high'
                            ? 'border-red-400 bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                            : p === 'medium'
                            ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                            : 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                          : 'border-transparent bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date picker */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tasks.modals.whenShouldBeDone', { defaultValue: 'When should this be done?' })}
                </p>
                <div className="grid grid-cols-4 gap-1.5 mb-3">
                  {days7.map(day => (
                    <button
                      key={day.date}
                      type="button"
                      onClick={() => { setFollowUpSelectedDate(day.date); setFollowUpCustomDate('') }}
                      className={`p-2 border-2 rounded-lg text-center text-xs transition-all ${
                        followUpSelectedDate === day.date
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                          : day.isToday
                          ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 text-orange-700 hover:border-orange-400'
                          : day.isTomorrow
                          ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 hover:border-emerald-400'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{t('tasks.modals.orPickDate', { defaultValue: 'or pick a date:' })}</span>
                  <input
                    type="date"
                    value={followUpCustomDate}
                    onChange={(e) => { setFollowUpCustomDate(e.target.value); setFollowUpSelectedDate('') }}
                    className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </>
          )}

          <ModalFooter left={<span className="text-xs text-gray-400">⌘ + Enter to complete with follow-up</span>}>
            <button onClick={completeOnly} disabled={submitting} className={buttonVariants.ghost}>
              {t('tasks.modals.justComplete', { defaultValue: 'Just complete' })}
            </button>
            {followUpDescription.trim() && (
              <button onClick={completeWithFollowUp} disabled={submitting} className={buttonVariants.success}>
                {submitting ? 'Saving…' : t('tasks.modals.completeAndFollowUp', { defaultValue: 'Complete & follow up' })}
              </button>
            )}
          </ModalFooter>
        </div>
      )}
    </ModalShell>
  )
}

export default CompleteTaskModal
